/**
 * Editor draft state for one List Group — seeded from the selected group or
 * blank for ?edit=new; owns validation, save/delete orchestration and toasts.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useEffect, useMemo, useState } from 'react';

import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { useListGroupMutations } from '../../useListGroups';
import {
  LIST_GROUP_DEFAULT_COLOR,
  type FlatListGroupRule,
  type ListGroup,
} from '../../list-groups.types';

export interface ListGroupDraft {
  name: string;
  color: string;
  rule: FlatListGroupRule;
}

const EMPTY_RULE: FlatListGroupRule = { type: 'and', nodes: [] };

export function toFlatRule(node: ListGroup['ruleJson'] | null): FlatListGroupRule {
  if (!node) return EMPTY_RULE;
  if (node.type === 'and' || node.type === 'or') {
    const nodes = node.nodes.filter(
      (n) => n.type === 'lists' || n.type === 'tag' || n.type === 'name' || n.type === 'metric',
    );
    return { type: node.type, nodes } as FlatListGroupRule;
  }
  if (node.type === 'lists' || node.type === 'tag' || node.type === 'name' || node.type === 'metric')
    return { type: 'and', nodes: [node] };
  return EMPTY_RULE;
}

function seed(group: ListGroup | null): ListGroupDraft {
  return {
    name: group?.name ?? '',
    color: group?.color ?? LIST_GROUP_DEFAULT_COLOR,
    rule: toFlatRule(group?.ruleJson ?? null),
  };
}

export function useListGroupEditorState(params: {
  group: ListGroup | null;
  isNew: boolean;
  countryKey: string;
  onDone: (created?: ListGroup) => void;
}) {
  const { group, isNew, countryKey, onDone } = params;
  const { create, update, remove } = useListGroupMutations();
  const { success, error } = useToast();
  const [draft, setDraft] = useState<ListGroupDraft>(() => seed(group));
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    setDraft(seed(group));
    setConfirmingDelete(false);
  }, [group?.id, isNew]);

  const valid = useMemo(
    () => draft.name.trim().length > 0 && draft.rule.nodes.length > 0,
    [draft],
  );
  const busy = create.isPending || update.isPending || remove.isPending;

  const save = async () => {
    try {
      if (isNew || !group) {
        const created = (await create.mutateAsync({
          countryKey,
          name: draft.name.trim(),
          color: draft.color,
          ruleJson: draft.rule,
        })) as ListGroup;
        success('Group created', `"${draft.name.trim()}" is ready to use.`);
        onDone(created);
        return;
      }
      if (group) {
        await update.mutateAsync({
          id: group.id,
          name: draft.name.trim(),
          color: draft.color,
          ruleJson: draft.rule,
        });
        success('Group saved', 'Membership re-resolves at next use.');
      }
      onDone();
    } catch (err) {
      error('Could not save the group', getApiErrorMessage(err));
    }
  };

  const removeGroup = async () => {
    if (!group) return;
    try {
      await remove.mutateAsync(group.id);
      success('Group deleted', `"${group.name}" is gone.`);
      onDone();
    } catch (err) {
      error('Could not delete the group', getApiErrorMessage(err));
    }
  };

  return {
    draft,
    setDraft,
    valid,
    busy,
    save,
    removeGroup,
    confirmingDelete,
    setConfirmingDelete,
  };
}
